import type { CreditPackageVo } from "@/api";
import creditIcon from "@/assets/images/credit-icon.png";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { useToast } from "@/component/Toast/useToast";
import { userStore } from "@/store/user";
import { formatNumberWithCommas } from "@/util/formatNumber";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import Image from "next/image";
import Link from "next/link";
import React from "react";

interface CreditPackageSectionProps {
  creditPackageList: CreditPackageVo[];
  currentPlanCode?: string;
}

export const CreditPackageSection: React.FC<CreditPackageSectionProps> =
  observer(({ creditPackageList, currentPlanCode }) => {
    const { error: showErrorToast } = useToast();
    const isStudioUser = userStore.userPlan?.memberShipName === "Studio";

    const formatDiscountedPrice = (price: number) => {
      const discounted = price * 0.75;
      if (discounted % 1 === 0) return discounted.toFixed(0);
      return discounted.toFixed(2).replace(/\.?0+$/, "");
    };

    return (
      <div className="mt-20 text-center">
        <h2
          id="add-ons"
          className="text-2xl font-bold text-text-primary leading-normal scroll-mt-[100px]"
        >
          Need More Credits? Get Add-ons
        </h2>
        <p className="text-text-tertiary text-xs font-normal mt-2">
          *Credits require an active subscription and are valid for 12 months.
        </p>

        <div className="mt-[30px] px-5 xl:px-0 max-w-4xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {creditPackageList.map((item, index) => (
              <Link
                key={`credit-${item.priceId}`}
                href={`/payment/sessions/checkout?priceId=${item.priceId}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  logger.event(VidMuseEvent.PricingPageCreditPackageClick, {
                    price: item.price,
                    currentPlanCode,
                  });

                  if (currentPlanCode === "free_plan") {
                    e.preventDefault();
                    showErrorToast("Credits require an active subscription");
                    return;
                  }
                }}
              >
                <div className="relative rounded-xl overflow-hidden shadow-[0_3.45px_29.785px_0_rgba(0,85,255,0.01)] hover:opacity-80">
                  {isStudioUser && (
                    <div className="absolute top-0 left-0 py-[1.5px] px-[10px] flex items-center justify-center text-white text-xs font-bold bg-[#FF6752] rounded-br-xl">
                      -25%
                    </div>
                  )}

                  <div className="p-5 pt-6 bg-background-card flex flex-col items-center">
                    <div className="flex items-center gap-2">
                      <Image
                        src={creditIcon}
                        alt="credit"
                        width={24}
                        height={24}
                      />
                      <span className="text-[#A7B0FF] text-[28px] font-bold">
                        {formatNumberWithCommas(Number(item.amount) || 0)}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <p
                        className={classNames(
                          "text-text-tertiary text-xl font-medium",
                          isStudioUser && "line-through text-sm opacity-60",
                        )}
                      >
                        ${formatNumberWithCommas(Number(item.price) || 0)}
                      </p>
                      {isStudioUser && (
                        <p className="text-text-tertiary text-xl font-medium">
                          $
                          {formatNumberWithCommas(
                            formatDiscountedPrice(Number(item.price) || 0),
                          )}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="h-10 bg-primary text-white flex items-center justify-center font-medium">
                    Buy Now
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  });
